import { RouteProp, useRoute } from "@react-navigation/native";
import { Provider } from "jotai";
import { Suspense, useEffect } from "react";
import { useQueryLoader } from "react-relay";

import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import { convertDateObjToServerStringFormat } from "@whoosh/utilities/src/dates";

import { ClubStackNavigatorParamList } from "../../navigators/ClubNavigator";
import { RequestBookingWizardQueryRenderer } from "./components/BookingWizardQueryRenderer/RequestBookingWizardQueryRenderer";
import { RequestBookingWizardQueryRendererQueryString } from "./components/BookingWizardQueryRenderer/RequestBookingWizardQueryRenderer";
import { RequestBookingWizardQueryRendererQuery } from "./components/BookingWizardQueryRenderer/__generated__/RequestBookingWizardQueryRendererQuery.graphql";
import { BookingWizard } from "./types";

type RequestBookingWizardProps = {
  isOpen: boolean;
  onClose: () => void;
  slotId: string | null;
  membershipTypeId: string | null;
  currentPatronId: string | null;
  clubSlug: string;
  requestedTime: string;
  minTime?: string;
  maxTime?: string;
  queueSlotId: string;
  initializationOverides?: BookingWizard.BookingWizardInitializationOverides;
};

export const RequestBookingWizard = ({
  isOpen,
  onClose,
  slotId,
  membershipTypeId,
  currentPatronId,
  clubSlug,
  requestedTime,
  minTime,
  maxTime,
  queueSlotId,
  initializationOverides,
}: RequestBookingWizardProps) => {
  return (
    <StandardModal
      isOpen={isOpen}
      onClose={() => {}}
      hideCloseButton={true}
      disableContentScroll={true}
      content={
        <RequestBookingWizardContent
          currentPatronId={currentPatronId}
          onClose={onClose}
          slotId={slotId}
          isOpen={isOpen}
          membershipTypeId={membershipTypeId}
          clubSlug={clubSlug}
          requestedTime={requestedTime}
          minTime={minTime}
          maxTime={maxTime}
          queueSlotId={queueSlotId}
          initializationOverides={{
            ...initializationOverides,
            groupSize:
              initializationOverides?.groupSize === 0
                ? 1
                : initializationOverides?.groupSize,
          }}
        />
      }
    />
  );
};

type RequestBookingWizardContentProps = RequestBookingWizardProps;

const RequestBookingWizardContent = ({
  isOpen,
  onClose,
  membershipTypeId,
  currentPatronId,
  clubSlug,
  requestedTime,
  minTime,
  maxTime,
  queueSlotId,
  initializationOverides,
}: RequestBookingWizardContentProps) => {
  // Request booking query loader
  const [queryRef, loadQuery] =
    useQueryLoader<RequestBookingWizardQueryRendererQuery>(
      RequestBookingWizardQueryRendererQueryString
    );

  const route = useRoute<RouteProp<ClubStackNavigatorParamList, "Agenda">>();
  const date = route?.params?.date ?? "";

  const currentDate = date
    ? date
    : convertDateObjToServerStringFormat(new Date());

  useEffect(() => {
    if (isOpen && queueSlotId && membershipTypeId) {
      // Load request booking query
      loadQuery(
        {
          queueSlotId,
          players: [currentPatronId],
          currentDate,
          clubSlug,
          slotType: "QUEUE",
          bookingType: "STANDARD",
        },
        { fetchPolicy: "network-only" }
      );
    }
  }, [
    loadQuery,
    isOpen,
    membershipTypeId,
    currentPatronId,
    currentDate,
    clubSlug,
    queueSlotId,
  ]);

  return (
    <Provider>
      <Suspense fallback={null}>
        {queryRef ? (
          <RequestBookingWizardQueryRenderer
            queryRef={queryRef}
            onClose={onClose}
            requestedTime={requestedTime}
            minTime={minTime}
            maxTime={maxTime}
            queueSlotId={queueSlotId}
            initializationOverides={initializationOverides}
          />
        ) : null}
      </Suspense>
    </Provider>
  );
};
